"use client";

/**
 * The tiles and the `<audio>`. Rendered by the provider, exactly once, and by
 * nothing else — see the provider's own comment for what happens when a variant
 * owns one instead.
 *
 * It BRANCHES on minimised rather than being swapped for a second component,
 * which is the whole point: a minimised call is one video with the chrome
 * floating over it, not a shrunken copy of the full layout (stacking every tile
 * into a 320px card crops each one to a slice of forehead), and it shows the
 * OTHER person, falling back to self only when alone — when the call is out of
 * the way, what you want to keep an eye on is them. Two layouts, one component
 * instance, one `<audio>` element for the session's whole life.
 */
import { Avatar, AvatarFallback } from "@repo/ui/components/ui/avatar";
import { cn } from "@repo/ui/lib/utils";
import { Video } from "lucide-react";
import { createPortal } from "react-dom";
import { useCall } from "./context";
import { type ParticipantWithVideo, participantsWithVideo } from "./participant-roster";

export function CallStage() {
  const {
    tiles,
    roster,
    cameraOn,
    speaking,
    status,
    geometry,
    bindTile,
    audioRef,
    previewReady,
    bindPreview,
    deviceErrors,
  } = useCall();

  if (typeof document === "undefined") return null;

  const content = tiles.filter((tile) => tile.isContent);
  const participants = participantsWithVideo(roster, tiles, cameraOn);
  const featured = participants.find((participant) => !participant.isLocal) ?? participants[0];

  // Presence owns the boxes: camera-off must neither evict a colleague nor
  // promote self into their place. Beside a share everyone is in the filmstrip,
  // so a floating self card would cover it rather than make room.
  const remotes = participants.filter((participant) => !participant.isLocal);
  const selfCard =
    content.length === 0 && remotes.length > 0 ? participants.find((participant) => participant.isLocal) : undefined;
  const gridTiles = selfCard ? remotes : participants;
  const sharing = content.length > 0;
  const solo = !sharing && gridTiles.length === 1;

  return createPortal(
    <div
      data-testid="call-stage"
      className={cn(
        "fixed z-30 flex overflow-hidden",
        // The stage's edge moves when the waiting room slides, and the two have
        // to move together or the column appears to uncover the video rather
        // than push it. Never while minimised: the card is dragged, and a
        // transition on a drag lags the pointer.
        geometry.minimised ? "" : "transition-[left,right,width] duration-200 ease-out motion-reduce:transition-none",
        // Expanded sits inside the app, so it wears the app's colours. The
        // minimised card floats over page content and keeps its own dark
        // surface for contrast.
        geometry.minimised
          ? "aspect-video w-80 rounded-2xl bg-neutral-950 text-neutral-100 shadow-2xl ring-1 ring-white/10"
          : "flex-col bg-muted text-foreground",
      )}
      style={geometry.stageStyle}
    >
      {geometry.minimised ? (
        content[0] ? (
          <video
            key={`featured-${content[0].tileId}`}
            ref={bindTile(content[0].tileId)}
            autoPlay
            playsInline
            muted={content[0].isLocal}
            className="size-full object-contain"
          />
        ) : featured ? (
          <div
            className={cn(
              "relative size-full overflow-hidden rounded-2xl outline-2 -outline-offset-2 outline-transparent",
              speaking.has(featured.attendeeId) && "outline-emerald-400",
            )}
          >
            <ParticipantMedia participant={featured} />
          </div>
        ) : previewReady ? (
          <video key="preview" ref={bindPreview} autoPlay playsInline muted className="size-full object-cover" />
        ) : (
          <div className="grid size-full place-items-center text-xs text-neutral-500">
            {status === "live" ? "Waiting for video…" : "Connecting…"}
          </div>
        )
      ) : (
        // Padding clears both strips, so the video is inset between them and
        // never runs under either. That inset IS the arrangement: chrome in its
        // own strips, video in the middle, nothing overlapping anything.
        <>
          <div
            className={cn(
              "flex min-h-0 flex-1 gap-2 px-2 pb-[3.75rem] pt-[2.75rem]",
              // A SHARE TAKES WIDTH FROM THE CAMERAS, NOT HEIGHT. The region is
              // wide and short and a slide is wide, so height is the scarce
              // dimension: a 96px strip along the bottom costs a 16:10 share
              // about a third of its area here, where a 176px column costs it
              // almost nothing.
              sharing ? "flex-row" : "flex-col",
            )}
          >
            {content.length > 0 && (
              // `min-w-0` matters once this is a row: without it the share
              // refuses to shrink past its video's intrinsic width and pushes
              // the camera column off the edge.
              <div className="min-h-0 min-w-0 flex-1">
                {content.map((tile) => (
                  <div key={tile.tileId} className="relative h-full overflow-hidden rounded-2xl bg-black">
                    {/* Letterboxed, never cropped — a cropped waveform is worse than useless. */}
                    {/* biome-ignore lint/a11y/useMediaCaption: a live WebRTC stream has no track to caption. Captions for a consult would come from live transcription rendered as text, not a <track>. */}
                    <video ref={bindTile(tile.tileId)} autoPlay playsInline className="size-full object-contain" />
                    <Nameplate>{tile.isLocal ? "You are sharing" : `${tile.name} is sharing`}</Nameplate>
                  </div>
                ))}
              </div>
            )}

            <div
              className={cn(
                "relative flex min-h-0 flex-col gap-2",
                // It scrolls rather than shrinking its tiles: a face squeezed
                // to fit eight of them is no longer a face worth showing.
                //
                // THE FADE IS THE AFFORDANCE. A scroll container just clips,
                // and a tile sliced flat by the bottom edge reads as a broken
                // layout rather than as "there are more". The mask softens that
                // edge so the cut is legible as an overflow, and snapping stops
                // a tile coming to rest half-shown. Tiles are top-aligned, so
                // when they all fit the fade falls on empty space and shows
                // nothing at all.
                sharing
                  ? cn(
                      "w-44 shrink-0 snap-y justify-start overflow-y-auto",
                      "[mask-image:linear-gradient(to_bottom,black_calc(100%-2rem),transparent)]",
                    )
                  : "flex-1 justify-center",
              )}
            >
              {rowsOf(gridTiles, columnsFor(gridTiles.length, sharing)).map((row, rowIndex) => (
                <div
                  // Rows are positional: a tile leaving reflows every row after
                  // it, so there is no stable id to key by and the index IS the
                  // identity. The tiles inside are keyed properly.
                  // biome-ignore lint/suspicious/noArrayIndexKey: the row is a layout slot, not a record.
                  key={rowIndex}
                  className={cn(
                    "flex min-h-0 items-center justify-center gap-2",
                    // In the column each row is one tile and keeps its own
                    // height; in the grid the rows split what there is.
                    sharing ? "shrink-0 snap-start" : "flex-1",
                  )}
                >
                  {row.map((participant) => (
                    <div
                      key={participant.attendeeId}
                      style={
                        solo || sharing ? undefined : { width: widthForColumns(columnsFor(gridTiles.length, sharing)) }
                      }
                      className={cn(
                        "relative max-h-full overflow-hidden rounded-2xl bg-black ring-2 ring-transparent transition",
                        speaking.has(participant.attendeeId) && "ring-emerald-400",
                        // Three sizings, and which one applies is the whole of
                        // the layout:
                        //
                        // SOLO fills the room up to 2:1 — height drives, width
                        // follows, `max-w-full` stops it in a narrow region.
                        // Past 2:1 a face is a stripe with a corridor of empty
                        // room either side.
                        //
                        // THE COLUMN beside a share is width-driven off the
                        // column's own 11rem, one tile per row, and the column
                        // scrolls when there are more than fit.
                        //
                        // A GRID is width-driven and height-capped. Height-
                        // driven there overflows instead: two tiles at full
                        // height want 818px each in a 1420px row. When
                        // `max-h-full` bites, the box goes a little wider than
                        // 16:9 and `object-cover` crops the sides.
                        solo && "aspect-[2/1] h-full w-auto max-w-full",
                        sharing && "aspect-video w-full shrink-0",
                        !solo && !sharing && "aspect-video",
                      )}
                    >
                      <ParticipantMedia participant={participant} />
                    </div>
                  ))}
                </div>
              ))}

              {participants.length === 0 && previewReady && (
                <video
                  key="preview"
                  ref={bindPreview}
                  autoPlay
                  playsInline
                  muted
                  className="min-h-0 size-full rounded-2xl object-contain"
                />
              )}
              {participants.length === 0 && !previewReady && (
                <div className="flex flex-col items-center gap-3 text-muted-foreground">
                  <span className="grid size-14 place-items-center rounded-full bg-muted">
                    <Video className="size-6" />
                  </span>
                  <p className="text-sm">{status === "live" ? "Waiting for video…" : "Connecting to the room…"}</p>
                </div>
              )}
            </div>
          </div>

          {/*
           * SELF STAYS SECONDARY WHILE SOMEONE ELSE IS PRESENT. An equal-sized
           * tile of yourself takes half the room from the person you came to
           * see — so it becomes a card in the corner, and the grid is the other
           * people. Camera-off changes neither box. Alone, self is the grid:
           * a corner card floating over nothing would be absurd.
           *
           * ANCHORED TO THE STAGE, NOT TO THE TILES. It sits in the surface's own
           * bottom-right corner, clearing the toolbar, so it stays put when the
           * tiles reflow, when the featured box hits its width cap, and when the
           * waiting room slides. Pinned to the tiles it would wander with them.
           */}
          {selfCard && (
            <div
              className={cn(
                "absolute bottom-[4.25rem] right-4 z-10 aspect-video w-40 overflow-hidden rounded-xl bg-black shadow-lg ring-2 ring-transparent lg:w-48",
                speaking.has(selfCard.attendeeId) && "ring-emerald-400",
              )}
            >
              <ParticipantMedia participant={selfCard} />
            </div>
          )}
        </>
      )}

      {/*
       * Device failure belongs to the stage, which every composition has.
       * Keep it above the controls in both layouts, even when remote video is
       * present; an empty-state message alone would disappear when they join.
       * This is a limitation of our devices, not a failed connection.
       */}
      {(deviceErrors.camera || deviceErrors.microphone) && (
        <output
          className={cn(
            "pointer-events-none absolute inset-x-3 z-10 mx-auto w-fit max-w-[calc(100%-1.5rem)] rounded-lg bg-neutral-900/90 px-3 py-2 text-xs leading-snug text-neutral-100 shadow-sm",
            geometry.minimised ? "bottom-12" : "bottom-16",
          )}
        >
          {deviceErrors.camera && <span className="block">Camera unavailable. Call continues without video.</span>}
          {deviceErrors.microphone && <span className="block">Microphone unavailable. You can still listen.</span>}
        </output>
      )}

      {/*
       * THE ELEMENT THAT MUST NOT REMOUNT. `bindAudioElement` is called once,
       * against this node; replace the node and the binding points at a
       * detached element — remote audio dies while the call still looks
       * connected. Its constant `key` keeps it reconciled in place across both
       * layouts above, and the provider keeps this component mounted.
       */}
      {/* biome-ignore lint/a11y/useMediaCaption: remote meeting audio has no track to caption. */}
      <audio key="call-audio" data-testid="call-audio" ref={audioRef} className="hidden" />
    </div>,
    document.body,
  );
}

/** The gap between tiles, in px. Matches the `gap-2` on the rows. */
const TILE_GAP = 8;

/**
 * How many tiles go across. Derived from the COUNT, never from the available
 * width — `auto-fit` picks columns by width, which is what put three people in
 * one wide short row with a dead band above them, and left the trailing row of
 * five hugging the left edge.
 *
 * `ceil(sqrt(n))` gives 1, 2, 2, 2, 3, 3 for one through six: the squarest
 * arrangement, which is the one that leaves the least unused room.
 */
function columnsFor(count: number, sharing: boolean): number {
  // A share puts the cameras in a column down the side — one across, however
  // many people, because the share is what everyone is looking at.
  if (sharing) return 1;
  return Math.max(Math.ceil(Math.sqrt(count)), 1);
}

/** Chunks tiles into rows of `columns`, so the last row can centre itself. */
function rowsOf<T>(tiles: readonly T[], columns: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < tiles.length; i += columns) rows.push(tiles.slice(i, i + columns));
  return rows;
}

/** One tile's share of the row, gaps taken out first. */
function widthForColumns(columns: number): string {
  return `calc((100% - ${(columns - 1) * TILE_GAP}px) / ${columns})`;
}

/**
 * One line, always. `right-3` as well as `left-3` is what gives `truncate` a
 * width to work against — without it the plate sizes to its text and a long
 * name runs the width of the tile rather than being cut.
 */
function Nameplate({ children }: { children: React.ReactNode }) {
  return (
    <span className="absolute inset-x-3 bottom-3 w-fit max-w-[calc(100%-1.5rem)] truncate rounded-lg bg-black/60 px-2.5 py-1 text-xs font-medium text-white backdrop-blur-sm">
      {children}
    </span>
  );
}

/** Both media states live inside the same box, so a camera toggle cannot move people. */
function ParticipantMedia({ participant }: { participant: ParticipantWithVideo }) {
  const { bindTile } = useCall();
  const initials = participant.name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <>
      {participant.video ? (
        <video
          key={participant.video.tileId}
          ref={bindTile(participant.video.tileId)}
          autoPlay
          playsInline
          muted={participant.isLocal}
          className="size-full object-cover"
        />
      ) : (
        // Size containment belongs to this inset layer, so it cannot change the
        // tile's aspect ratio. cqmin scales against its shorter edge in every
        // layout, including the height-capped grid; inline dimensions override
        // Avatar's fixed size utilities without relying on data-size variants.
        // One tint for everyone: hue identifies practitioners on the schedule.
        // Inventing per-person hues here would contradict those app-owned accents.
        <div
          className="absolute inset-0 grid place-items-center bg-linear-to-br from-primary/45 via-primary to-primary/45"
          style={{ containerType: "size" }}
        >
          <Avatar style={{ width: "35cqmin", height: "35cqmin" }}>
            <AvatarFallback className="bg-primary-foreground text-primary" style={{ fontSize: "14cqmin" }}>
              {initials}
            </AvatarFallback>
          </Avatar>
        </div>
      )}
      <Nameplate>{participant.isLocal ? "You" : participant.name}</Nameplate>
    </>
  );
}
